import { useState } from "react";
import { useNavigate } from "react-router-dom";
import useStore from "../store/useStore";
import { toast } from "react-toastify";
import axios from "axios";
import config from "../config";
import { ToastContainer } from "react-toastify";
import SubscriptionBadgesProfile from "./PricingModalProfile";
import { AlertCircle } from "lucide-react";
import axiosInstance from "./AxiosInstance";
import { MapPin } from "lucide-react";

const MyProfile = () => {
  const navigate = useNavigate();
  const {
    userName,
    userEmail,
    company,
    jwt,
    subscriptionType,
    subscriptionStartDate,
    subscriptionEndDate,
    subscriptionStatus,
    setCompany,
  } = useStore();
  const [isChangingPassword, setIsChangingPassword] = useState(false);
  const [isManagingSubscription, setIsManagingSubscription] = useState(false);
  const [showConfirmModal, setShowConfirmModal] = useState(false);

  const [isLoading, setIsLoading] = useState(false);
  const [passwordForm, setPasswordForm] = useState({
    currentPassword: "",
    newPassword: "",
    confirmPassword: "",
  });
  const [passwordErrors, setPasswordErrors] = useState({
    newPassword: "",
    confirmPassword: "",
  });

  const [isEditingAddress, setIsEditingAddress] = useState(false);
  const [newAddress, setNewAddress] = useState("");

  const handleAddressSearch = () => {
    new window.daum.Postcode({
      oncomplete: function (data) {
        const addr = data.address;
        setNewAddress(addr);
      },
    }).open();
  };

  const handleAddressUpdate = async () => {
    try {
      const response = await axiosInstance.put("/users/company-address", {
        companyAddress: newAddress,
      });

      await setCompany(
        company.name,
        response.data.companyAddress,
        response.data.companyAddressName
      );

      setIsEditingAddress(false);
      toast.success("주소가 성공적으로 변경되었습니다");
    } catch (error) {
      console.error("주소 업데이트 실패:", error);
      toast.error("주소 업데이트에 실패했습니다");
    }
  };

  const validatePassword = (password) => {
    if (password.length < 8) {
      return "비밀번호는 최소 8자 이상이어야 합니다";
    }
    return "";
  };

  const handlePasswordChange = async (e) => {
    e.preventDefault();

    // 비밀번호 유효성 검사
    const newPasswordError = validatePassword(passwordForm.newPassword);
    if (newPasswordError) {
      setPasswordErrors((prev) => ({ ...prev, newPassword: newPasswordError }));
      return;
    }

    // 비밀번호 일치 검사
    if (passwordForm.newPassword !== passwordForm.confirmPassword) {
      setPasswordErrors((prev) => ({
        ...prev,
        confirmPassword: "새 비밀번호가 일치하지 않습니다",
      }));
      return;
    }

    setIsLoading(true);
    try {
      const response = await axios.post(
        `${config.apiUrl}/change/password`,
        {
          email: userEmail,
          currentPassword: passwordForm.currentPassword,
          newPassword: passwordForm.newPassword,
        },
        {
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${jwt}`,
          },
        }
      );

      if (response.status === 200) {
        toast.success("비밀번호가 성공적으로 변경되었습니다");
        setIsChangingPassword(false);
        setPasswordForm({
          currentPassword: "",
          newPassword: "",
          confirmPassword: "",
        });
        setPasswordErrors({
          newPassword: "",
          confirmPassword: "",
        });
      }
    } catch (error) {
      toast.error(
        error.response?.data?.message || "비밀번호 변경에 실패했습니다"
      );
    } finally {
      setIsLoading(false);
    }
  };

  const handleCancelSubscription = async () => {
    setIsLoading(true);
    try {
      const response = await axios.put(
        `${config.apiUrl}/subscriptions/cancel`,
        {}, // request body (비어있는 경우도 명시)
        {
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${jwt}`,
          },
        }
      );

      if (response.status === 200) {
        toast.success("구독이 성공적으로 취소되었습니다.");
      }
    } catch (error) {
      toast.error(error.response?.data?.message || "구독 취소에 실패했습니다");
    } finally {
      setIsLoading(false);
    }
  };

  const onCancelSubscription = () => {
    // setShowConfirmModal(true);
  };

  const handleManageSubscription = () => {
    setIsManagingSubscription(!isManagingSubscription); // 구독 관리 상태 토글
  };

  const [isChangingPayment, setIsChangingPayment] = useState(false);

  const [cardNumber, setCardNumber] = useState("");
  const [expiryDate, setExpiryDate] = useState("");
  const [cvc, setCvc] = useState("");

  const [error, setError] = useState("");
  const [cardType, setCardType] = useState("");

  // 카드 번호 포맷팅 및 유효성 검증
  const formatCardNumber = (value) => {
    const v = value.replace(/\s+/g, "").replace(/[^0-9]/gi, "");
    const matches = v.match(/\d{4,16}/g);
    const match = (matches && matches[0]) || "";
    const parts = [];

    for (let i = 0, len = match.length; i < len; i += 4) {
      parts.push(match.substring(i, i + 4));
    }

    if (parts.length) {
      return parts.join(" ");
    } else {
      return value;
    }
  };

  // 카드 타입 감지
  const detectCardType = (number) => {
    const re = {
      visa: /^4/,
      mastercard: /^5[1-5]/,
      amex: /^3[47]/,
    };

    for (const [type, regex] of Object.entries(re)) {
      if (regex.test(number)) {
        setCardType(type);
        return;
      }
    }
    setCardType("");
  };

  // 만료일 포맷팅
  const formatExpiryDate = (value) => {
    const v = value.replace(/\s+/g, "").replace(/[^0-9]/gi, "");
    if (v.length >= 2) {
      return `${v.slice(0, 2)}/${v.slice(2, 4)}`;
    }
    return v;
  };

  const handleCardNumberChange = (e) => {
    const value = formatCardNumber(e.target.value);
    setCardNumber(value);
    detectCardType(value.replace(/\s+/g, ""));
    setError("");
  };

  const handleExpiryDateChange = (e) => {
    const value = formatExpiryDate(e.target.value);
    if (value.length <= 5) {
      setExpiryDate(value);
      setError("");
    }
  };

  const handleCvcChange = (e) => {
    const value = e.target.value.replace(/[^0-9]/g, "");
    if (value.length <= 4) {
      setCvc(value);
      setError("");
    }
  };

  const validateForm = () => {
    const now = new Date();
    const currentYear = now.getFullYear() % 100;
    const currentMonth = now.getMonth() + 1;

    if (cardNumber.replace(/\s+/g, "").length < 16) {
      setError("올바른 카드 번호를 입력해주세요.");
      return false;
    }

    if (expiryDate.length < 5) {
      setError("만료일을 입력해주세요.");
      return false;
    }

    const [month, year] = expiryDate.split("/").map((num) => parseInt(num, 10));

    if (month < 1 || month > 12) {
      setError("올바른 만료 월을 입력해주세요.");
      return false;
    }

    if (year < currentYear || (year === currentYear && month < currentMonth)) {
      setError("만료된 카드입니다.");
      return false;
    }

    if (cvc.length < 3) {
      setError("올바른 CVC를 입력해주세요.");
      return false;
    }

    return true;
  };

  const handlePaymentSubmit = async (e) => {
    e.preventDefault();
    setError("");

    if (!validateForm()) {
      return;
    }

    setIsLoading(true);
    try {
      // 실제 API 호출 로직
      await new Promise((resolve) => setTimeout(resolve, 1500)); // 시뮬레이션
      setIsChangingPayment(false);
      // 성공 토스트 메시지 표시
    } catch (err) {
      setError("결제 수단 변경 중 오류가 발생했습니다. 다시 시도해주세요.");
    } finally {
      setIsLoading(false);
    }
  };

  const getStatusStyles = (status) => {
    switch (status) {
      case "ACTIVE":
        return "bg-green-50 text-green-700";
      case "CANCELLED":
        return "bg-orange-50 text-orange-700";
      case "EXPIRED":
        return "bg-red-50 text-red-700";
      case "INACTIVE":
        return "bg-gray-50 text-gray-600";
      default:
        return "bg-gray-50 text-gray-600";
    }
  };
  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return `${date.getFullYear()}년 ${
      date.getMonth() + 1
    }월 ${date.getDate()}일`;
  };

  return (
    <div className="min-h-screen bg-gray-50 pt-20">
      <ToastContainer
        position="top-center"
        autoClose={3000}
        hideProgressBar={false}
        newestOnTop
        closeOnClick
        rtl={false}
        pauseOnFocusLoss
        draggable
        pauseOnHover
        theme="colored"
      />
      <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 py-10">
        <div className="bg-white rounded-2xl shadow-sm overflow-hidden">
          {/* 프로필 헤더 */}
          <div className="bg-gradient-to-r from-sky-950 to-blue-900 px-8 py-10">
            <div className="flex justify-between items-start">
              {" "}
              {/* justify-between 추가 */}
              <div className="flex items-center space-x-4">
                <div className="w-20 h-20 bg-white rounded-full flex items-center justify-center">
                  <span className="text-3xl font-semibold text-blue-600">
                    {userName?.charAt(0)}
                  </span>
                </div>
                <div>
                  <h1 className="text-2xl font-bold text-white">{userName}</h1>
                  <p className="text-blue-100 mt-1">{userEmail}</p>
                </div>
              </div>
              {/* 뒤로가기 버튼 */}
              <button
                onClick={() => navigate(-1)}
                className="flex items-center gap-2 px-4 py-2 bg-white/10 rounded-lg text-white hover:bg-white/20 transition-colors"
              >
                <svg
                  className="w-5 h-5"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth="2"
                    d="M15 19l-7-7 7-7"
                  />
                </svg>
                뒤로가기
              </button>
            </div>
          </div>

          {/* 프로필 정보 */}
          <div className="px-8 py-6 space-y-8">
            {/* 회사 정보 */}
            <div>
              <h2 className="text-lg font-semibold text-gray-900 mb-4">
                회사 정보
              </h2>
              <div className="bg-gray-50 rounded-lg p-4 space-y-3">
                <div>
                  <label className="text-sm text-gray-500">회사명</label>
                  <p className="text-gray-900 mt-1">{company.name}</p>
                </div>
                <div className="bg-white rounded-lg shadow-sm p-6 border border-gray-100">
                  <div className="flex items-center justify-between mb-4">
                    <div className="flex items-center gap-2">
                      <MapPin className="w-5 h-5 text-gray-400" />
                      <h3 className="text-lg font-semibold text-gray-900">
                        주소 정보
                      </h3>
                    </div>
                    {!isEditingAddress && (
                      <button
                        onClick={() => {
                          setIsEditingAddress(true);
                          setNewAddress(company.addressName);
                        }}
                        className="text-sm text-blue-600 hover:text-blue-700 font-medium flex items-center gap-1.5"
                      >
                        <svg
                          className="w-4 h-4"
                          fill="none"
                          stroke="currentColor"
                          viewBox="0 0 24 24"
                        >
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth={2}
                            d="M15.232 5.232l3.536 3.536m-2.036-5.036a2.5 2.5 0 113.536 3.536L6.5 21.036H3v-3.572L16.732 3.732z"
                          />
                        </svg>
                        수정
                      </button>
                    )}
                  </div>

                  {isEditingAddress ? (
                    <div className="space-y-4">
                      <div className="relative">
                        <input
                          type="text"
                          value={newAddress}
                          readOnly
                          className="w-full pl-10 pr-4 py-3 rounded-lg bg-gray-50 border border-gray-200 text-gray-900 cursor-pointer hover:bg-gray-100 transition-colors"
                          placeholder="주소 검색을 클릭하세요"
                          onClick={handleAddressSearch}
                        />
                        <MapPin className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400" />
                      </div>

                      <div className="flex items-center gap-3">
                        <button
                          onClick={handleAddressUpdate}
                          className="flex-1 py-2.5 bg-blue-600 text-white rounded-lg hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 transition-colors disabled:bg-gray-400 disabled:cursor-not-allowed font-medium"
                          disabled={!newAddress}
                        >
                          변경사항 저장
                        </button>
                        <button
                          onClick={() => setIsEditingAddress(false)}
                          className="flex-1 py-2.5 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 transition-colors font-medium"
                        >
                          취소
                        </button>
                      </div>
                    </div>
                  ) : (
                    <div className="bg-gray-50 rounded-lg p-4">
                      <p className="text-gray-700">{company.addressName}</p>
                    </div>
                  )}
                </div>
              </div>
            </div>
            {/* 현재 구독 정보 */}
            <div className="space-y-4">
              {/* 현재 구독 카드 */}
              <div className="bg-white rounded-lg shadow-sm p-6">
                <div className="flex items-center justify-between mb-4">
                  <h2 className="text-xl font-bold text-gray-900">현재 구독</h2>
                  <span
                    className={`inline-flex items-center px-2.5 py-1 rounded-full text-sm font-medium ${getStatusStyles(
                      subscriptionStatus
                    )}`}
                  >
                    <svg
                      className="w-3 h-3 mr-1"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M5 13l4 4L19 7"
                      />
                    </svg>
                    {subscriptionStatus}
                  </span>
                </div>

                <div className="flex items-center justify-between">
                  <div className="space-y-2">
                    <SubscriptionBadgesProfile
                      subscriptionType={subscriptionType}
                    />
                  </div>
                  <button
                    onClick={handleManageSubscription}
                    className="inline-flex items-center px-4 py-2 text-sm font-medium text-blue-600 bg-white border border-blue-200 rounded-lg hover:bg-blue-50 transition-colors duration-200"
                  >
                    구독 관리
                    <svg
                      className="w-4 h-4 ml-1"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M9 5l7 7-7 7"
                      />
                    </svg>
                  </button>
                </div>
              </div>

              {/* 구독 관리 상세 정보 */}
              {isManagingSubscription && (
                <div className="bg-gray-50 rounded-lg shadow-sm p-6">
                  <h3 className="text-lg font-semibold text-gray-900 mb-6">
                    구독 상세 정보
                  </h3>

                  <div className="space-y-6">
                    <div className="flex items-start gap-3">
                      <svg
                        className="w-5 h-5 mt-1 text-gray-500"
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={2}
                          d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z"
                        />
                      </svg>
                      {subscriptionStartDate &&
                      subscriptionEndDate &&
                      subscriptionType !== "free" ? (
                        <div className="space-y-3">
                          <div>
                            <p className="font-medium text-gray-900">
                              구독 시작일
                            </p>
                            <p className="text-sm text-gray-500 mt-1">
                              {formatDate(subscriptionStartDate)}
                            </p>
                          </div>
                          <div>
                            <p className="font-medium text-gray-900">
                              다음 결제일
                            </p>
                            <p className="text-sm text-gray-500 mt-1">
                              {formatDate(subscriptionEndDate)}
                            </p>
                          </div>
                        </div>
                      ) : (
                        <p className="text-gray-500">무료 체험 이용 중</p>
                      )}
                    </div>
                  </div>
                  {/* 구독 취소 버튼 추가 */}
                  <div className="pt-4 mt-6 border-t border-gray-200">
                    <button
                      onClick={handleCancelSubscription}
                      className="w-full px-4 py-2.5 text-sm text-red-600 hover:bg-red-50 rounded-lg transition-colors border border-red-200 flex items-center justify-center gap-2"
                    >
                      <AlertCircle className="w-4 h-4" />
                      구독 취소
                    </button>
                  </div>
                </div>
              )}
            </div>

            {/* 구독 취소 확인 모달 */}
            {showConfirmModal && (
              <div className="fixed inset-0 flex items-center justify-center z-50">
                <div
                  className="absolute inset-0 bg-black bg-opacity-50"
                  onClick={() => setShowConfirmModal(false)}
                />
                <div className="relative bg-white rounded-lg shadow-xl max-w-md w-full mx-4 p-6">
                  <div className="flex items-center justify-center w-12 h-12 mx-auto mb-4 rounded-full bg-red-100">
                    <AlertCircle className="w-6 h-6 text-red-600" />
                  </div>
                  <h3 className="text-lg font-semibold text-center mb-2">
                    구독을 취소하시겠습니까?
                  </h3>
                  <p className="text-gray-500 text-sm text-center mb-6">
                    구독 취소 시 현재 구독 기간이 종료될 때까지 서비스를
                    이용하실 수 있습니다. 기간 종료 후에는 서비스 이용이
                    제한됩니다.
                  </p>
                  <div className="flex gap-3">
                    <button
                      onClick={() => setShowConfirmModal(false)}
                      className="flex-1 px-4 py-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 transition-colors"
                    >
                      돌아가기
                    </button>
                    <button
                      onClick={() => {
                        handleCancelSubscription();
                        setShowConfirmModal(false);
                      }}
                      className="flex-1 px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors"
                    >
                      구독 취소
                    </button>
                  </div>
                </div>
              </div>
            )}
            {/* 비밀번호 변경 */}
            <div className="">
              <div className="flex items-center justify-between mb-6">
                <div>
                  <h2 className="text-lg font-semibold text-gray-900">
                    비밀번호 변경
                  </h2>
                  <p className="text-sm text-gray-500 mt-1">
                    주기적인 비밀번호 변경을 통해 계정을 안전하게 보호하세요
                  </p>
                </div>
                <button
                  onClick={() => {
                    setIsChangingPassword(!isChangingPassword);
                    setPasswordForm({
                      currentPassword: "",
                      newPassword: "",
                      confirmPassword: "",
                    });
                    setPasswordErrors({
                      newPassword: "",
                      confirmPassword: "",
                    });
                  }}
                  className={`px-4 py-2 rounded-lg transition-colors ${
                    isChangingPassword
                      ? "text-gray-700 bg-gray-100 hover:bg-gray-200"
                      : "text-white bg-blue-600 hover:bg-blue-700"
                  }`}
                  disabled={isLoading}
                >
                  {isChangingPassword ? "취소" : "비밀번호 변경"}
                </button>
              </div>

              {isChangingPassword && (
                <form onSubmit={handlePasswordChange} className="space-y-6">
                  <div className="space-y-4">
                    {/* 현재 비밀번호 입력 */}
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        현재 비밀번호
                      </label>
                      <input
                        type="password"
                        value={passwordForm.currentPassword}
                        onChange={(e) =>
                          setPasswordForm((prev) => ({
                            ...prev,
                            currentPassword: e.target.value,
                          }))
                        }
                        className="w-full px-4 py-3 rounded-lg border border-gray-300 focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all"
                        placeholder="현재 비밀번호를 입력하세요"
                        disabled={isLoading}
                      />
                    </div>

                    {/* 새 비밀번호 입력 */}
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        새 비밀번호
                      </label>
                      <input
                        type="password"
                        value={passwordForm.newPassword}
                        onChange={(e) => {
                          setPasswordForm((prev) => ({
                            ...prev,
                            newPassword: e.target.value,
                          }));
                          setPasswordErrors((prev) => ({
                            ...prev,
                            newPassword: validatePassword(e.target.value),
                          }));
                        }}
                        className={`w-full px-4 py-3 rounded-lg border transition-all ${
                          passwordErrors.newPassword
                            ? "border-red-300 focus:ring-red-500"
                            : "border-gray-300 focus:ring-blue-500"
                        } focus:ring-2 focus:border-transparent`}
                        placeholder="새 비밀번호를 입력하세요"
                        disabled={isLoading}
                      />
                      {passwordErrors.newPassword && (
                        <p className="mt-2 text-sm text-red-600">
                          {passwordErrors.newPassword}
                        </p>
                      )}
                    </div>

                    {/* 새 비밀번호 확인 */}
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        새 비밀번호 확인
                      </label>
                      <input
                        type="password"
                        value={passwordForm.confirmPassword}
                        onChange={(e) =>
                          setPasswordForm((prev) => ({
                            ...prev,
                            confirmPassword: e.target.value,
                          }))
                        }
                        className={`w-full px-4 py-3 rounded-lg border transition-all ${
                          passwordErrors.confirmPassword
                            ? "border-red-300 focus:ring-red-500"
                            : "border-gray-300 focus:ring-blue-500"
                        } focus:ring-2 focus:border-transparent`}
                        placeholder="새 비밀번호를 다시 입력하세요"
                        disabled={isLoading}
                      />
                      {passwordErrors.confirmPassword && (
                        <p className="mt-2 text-sm text-red-600">
                          {passwordErrors.confirmPassword}
                        </p>
                      )}
                    </div>
                  </div>

                  {/* 변경하기 버튼 */}
                  <div className="flex justify-end">
                    <button
                      type="submit"
                      className="px-6 py-2.5 bg-blue-600 text-white rounded-lg hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 transition-colors disabled:bg-gray-400"
                      disabled={
                        isLoading ||
                        !passwordForm.currentPassword ||
                        !passwordForm.newPassword ||
                        !passwordForm.confirmPassword
                      }
                    >
                      {isLoading ? "변경 중..." : "변경하기"}
                    </button>
                  </div>
                </form>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default MyProfile;
